// Finance ledger types. Mirrors the backend transaction_type Postgres enum,
// the category/source typed-strings (apps/backend/internal/modules/finance/
// domain/transaction.go) and the P&L DTOs (pnl_dto.go).

export type TransactionType = 'income' | 'expense'

export const INCOME_CATEGORIES = [
  'storage_rental',
  'service_projects',
  'parts_sales',
  'boat_sales',
  'consignment_commission',
  'other_income',
] as const

export const EXPENSE_CATEGORIES = [
  'parts_supplies',
  'labor',
  'boat_cogs',
  'payroll',
  'utilities',
  'insurance',
  'marketing',
  'facility_maintenance',
  'professional_fees',
  'floor_plan_interest',
  'floor_plan_curtailment',
  'other_expense',
] as const

export type TransactionCategory =
  (typeof INCOME_CATEGORIES)[number] | (typeof EXPENSE_CATEGORIES)[number]

export type TransactionSource =
  | 'manual'
  | 'deal'
  | 'consignment'
  | 'boat_sale'
  | 'storage_fee'
  | 'work_order'
  | 'curtailment'
  | 'interest'

export interface Transaction {
  id: string
  occurred_on: string // YYYY-MM-DD
  type: TransactionType
  category: TransactionCategory
  amount: number
  description: string
  source: TransactionSource
  source_id?: string
  created_at: string
  updated_at: string
}

// Body for create/update of a ledger entry (manual fields only).
export interface TransactionWriteBody {
  id?: string // client-generated for offline create replay idempotency
  occurred_on: string
  type: TransactionType
  category: TransactionCategory
  amount: number
  description: string
}

// List filters for the transactions query (all optional).
export interface TransactionFilters {
  from?: string
  to?: string
  type?: TransactionType
  category?: TransactionCategory
  q?: string
}

export interface PnLLine {
  category: TransactionCategory
  label: string
  amount: number
  count: number
}

export interface PnL {
  from: string // YYYY-MM-DD
  to: string // YYYY-MM-DD
  revenue: PnLLine[]
  expenses: PnLLine[]
  total_revenue: number
  total_expenses: number
  net_income: number
  margin_pct: number
}

export interface MonthlyTotal {
  month: string // YYYY-MM
  income: number
  expense: number
  net: number
}

export const CATEGORY_LABELS: Record<TransactionCategory, string> = {
  storage_rental: 'Storage & slip rentals',
  service_projects: 'Service & project revenue',
  parts_sales: 'Parts & retail sales',
  boat_sales: 'Boat sales',
  consignment_commission: 'Consignment commissions',
  other_income: 'Other income',
  parts_supplies: 'Parts & supplies',
  labor: 'Labor',
  boat_cogs: 'Boat cost of sales',
  payroll: 'Payroll',
  utilities: 'Utilities',
  insurance: 'Insurance',
  marketing: 'Marketing',
  facility_maintenance: 'Facility maintenance',
  professional_fees: 'Professional fees',
  floor_plan_interest: 'Floor plan interest',
  floor_plan_curtailment: 'Floor plan curtailments',
  other_expense: 'Other expense',
}

export const SOURCE_LABELS: Record<TransactionSource, string> = {
  manual: 'Manual',
  deal: 'Deal',
  consignment: 'Consignment',
  boat_sale: 'Boat sale',
  storage_fee: 'Storage fee',
  work_order: 'Work order',
  curtailment: 'Floor plan curtailment',
  interest: 'Floor plan interest',
}

// Floor-plan exposure summary. Mirrors the Go
// finance/application/floorplan_summary.go FloorplanSummaryDTO.
export interface FloorplanLoanSummary {
  loan_id: string
  boat_id: string
  boat_label: string
  lender: string
  principal: number
  curtailment_amount: number
  interest_rate: number // annual percentage; 0 = not tracked
  frequency: 'monthly' | 'quarterly'
  status: 'active'
  next_due_on?: string // YYYY-MM-DD
  accrued_unpaid_total: number
  accrued_unpaid_count: number
  paid_total: number
  interest_paid_total: number // financing charges paid to date
  interest_unpaid_total: number // interest accrued unpaid
}

export interface FloorplanSummary {
  active_loans: number
  total_principal: number
  accrued_unpaid_total: number
  accrued_unpaid_count: number
  paid_this_month: number
  paid_year_to_date: number
  interest_paid_this_month: number
  interest_paid_year_to_date: number
  // Full cost of carrying the financed inventory: every curtailment plus
  // every interest charge paid on the active loans.
  total_financing_cost: number
  loans: FloorplanLoanSummary[]
}

// ---- Accounts receivable (invoices + payments) ----
// Mirrors the Postgres enums in migration
// 000034_create_invoicing_tables.up.sql and the Go typed-strings in
// apps/backend/internal/modules/finance/domain/invoice.go.

export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'void'
export type InvoiceSource = 'storage' | 'work_order' | 'boat_sale' | 'manual'
export type InvoiceItemCode =
  | 'storage_wet'
  | 'storage_dry'
  | 'storage_warehouse'
  | 'service_labor'
  | 'part'
  | 'boat_sale'
  | 'other'
export type PaymentMethod = 'cash' | 'check' | 'card' | 'ach' | 'other'

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  paid: 'Paid',
  void: 'Void',
}

export const INVOICE_STATUS_VARIANT: Record<
  InvoiceStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  paid: 'default',
  open: 'secondary',
  draft: 'outline',
  void: 'destructive',
}

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  check: 'Check',
  card: 'Card',
  ach: 'ACH',
  other: 'Other',
}

export interface InvoiceLine {
  id: string
  item_code: InvoiceItemCode
  item_label: string
  description: string
  quantity: number
  unit_price: number
  amount: number
  part_id?: string
}

export interface InvoicePayment {
  id: string
  invoice_id: string
  amount: number
  received_on: string // YYYY-MM-DD
  method: PaymentMethod
  reference?: string
  recorded_by?: string
  created_at: string
}

export interface Invoice {
  id: string
  customer_id: string
  customer_name: string
  number: string
  status: InvoiceStatus
  source: InvoiceSource
  source_id?: string
  issued_on: string // YYYY-MM-DD
  due_on?: string
  subtotal: number
  tax: number
  total: number
  amount_paid: number
  balance_due: number
  notes?: string
  overdue: boolean
  lines: InvoiceLine[]
  payments: InvoicePayment[]
  created_at: string
  updated_at: string
}

export interface InvoiceLineBody {
  item_code: InvoiceItemCode
  description: string
  quantity: number
  unit_price: number
}

export interface InvoiceCreateBody {
  customer_id: string
  due_on?: string
  notes?: string
  lines: InvoiceLineBody[]
}

export interface InvoiceUpdateBody {
  due_on?: string
  notes?: string
  lines?: InvoiceLineBody[]
}

export interface PaymentRecordBody {
  amount: number
  received_on: string
  method: PaymentMethod
  reference?: string
}

// Storage-invoicing health (the "set a payer" hint).
export interface StorageInvoicingStatus {
  needs_payer_count: number
}
